import { createContext } from 'react';

import { type Role } from './roles';

/** One organization the signed-in person belongs to, and their role in it. */
export interface Membership {
  id: string;
  name: string;
  slug: string;
  /** PENDING, ACTIVE, SUSPENDED or DEACTIVATED, as the backend reports it. */
  status: string;
  /** What they may do HERE. The same person may hold another role elsewhere. */
  role: Role;
}

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  /**
   * The role that applies right now: their role in the organization they are
   * currently working in, or Activist Staff for an internal account.
   *
   * Derived rather than sent as one value by the backend, because somebody may
   * be a Super Admin of one organization and an Admin of another. Switching
   * organization changes this.
   */
  role: Role;
  /**
   * Everywhere they can act. Empty for Activist staff, who belong to no
   * organization. One entry is the common case; several means they were asked
   * which one to open.
   */
  organizations: Membership[];
  /** Which of `organizations` they are working in, if any. */
  organizationId: string | null;
  organizationName: string | null;
}

export interface AuthContextValue {
  user: CurrentUser | null;
  /** True while the stored session is being read on first load. */
  loading: boolean;
  /** Records a successful sign-in. The tokens are already stored by then. */
  signIn: (user: CurrentUser) => void;
  /**
   * Switch to another of the person's organizations.
   *
   * Changes their current role to the one they hold there, and changes which
   * organization every subsequent request is about. An id they do not belong
   * to is ignored rather than trusted.
   */
  switchOrganization: (organizationId: string) => void;
  /** Clears the session and the cached data. */
  signOut: () => void;
}

/**
 * Kept in its own file, separate from the provider component.
 *
 * A file that exports both a component and a non-component value breaks Vite's
 * hot reloading, so the split is deliberate rather than tidiness.
 */
export const AuthContext = createContext<AuthContextValue | null>(null);
